import { type ActionFunctionArgs, redirect } from "react-router-dom";
import { segment, trackSubmitForm } from "../../segment";
import type { FormState, TrialType } from "../../types";
import { setDatadogUserInfo } from "../../utils/datadogRumInit.ts";
import postData from "./postData.ts";
import validateForm from "./validateForm.ts";
import { PRIMARY_PRODUCTS } from "../../data";

/**
 * Default form action handler for signup forms. Processes form submission, validates data,
 * integrates with analytics, and handles API communication.
 *
 * @template Form - Type extending FormState for specific form implementations
 * @param {ActionFunctionArgs} params - React Router action function arguments
 * @param {Request} params.request - Form submission request object
 *
 * @returns {Promise<{errors?: Record<string, string>, formState: Form}>} Object containing validation errors and form state
 * @throws {Response} Redirects to "/success" on successful form submission
 *
 * @example
 * ```typescript
 * // In a route definition:
 * {
 *   action: async (args: ActionFunctionArgs) => {
 *     const { errors, formState } = await defaultActionForm<FormState>(args);
 *     return { errors, formState };
 *   }
 * }
 */
export async function defaultActionForm<Form extends FormState = FormState>({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const trialType = (formData.get("trialType") as TrialType | null) ?? "mo";

  const primaryProductName = formData.get("primaryProductName") as string | null;

  /**
   * Workaround to replace label with the actual value.
   * TODO: remove when Select component is implemented properly.
   * @link https://spartacommodties.atlassian.net/browse/ORI-433
   */
  if (typeof primaryProductName === "string") {
    const primaryProduct = PRIMARY_PRODUCTS.find(
      (product) => product.name === primaryProductName || product.displayName === primaryProductName
    );

    if (primaryProduct) {
      formData.set("primaryProductName", primaryProduct.name);
    }
  }

  const { errors: errorForm, formState } = validateForm<Form>(formData);

  if (errorForm !== undefined) return { errors: errorForm, formState };

  if (formState?.email !== null) {
    // This is a test to check if we can decrease the amount of anonymous IDs.
    segment.load();
    segment.identify(formState?.email, {
      firstName: formState?.firstName,
      lastName: formState?.lastName,
      email: formState?.email,
      username: formState?.email,
    });
    setDatadogUserInfo(formState.email);
  }

  trackSubmitForm(formState, trialType);

  const { ok, errors } = await postData(formState);

  if (ok && errors === null) {
    throw redirect(`/success?trialType=${trialType}`);
  }

  return { errors, formState };
}
